import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import {
  PaginatedExpertsResponse,
  SearchExpertsParams,
  ExpertDetailsDto,
  CreateAppointmentDto,
  Appointment,
  Specialization,
  AvailableSlot,
  ExpertListItemDto, // Import ExpertListItemDto
} from '../types/expert';

// --- Expert Endpoints ---

export const searchExperts = async (params: SearchExpertsParams): Promise<PaginatedExpertsResponse> => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (key !== 'specializations' && value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  if (params.specializations) {
    params.specializations.forEach(spec => searchParams.append('specializations', spec));
  }
  const response = await api.get<ApiResponse<ExpertListItemDto[]>>(`/experts?${searchParams.toString()}`);
  const apiResponse = response.data; // Це об'єкт { success, data, meta }

  const experts = apiResponse.data; // Це масив ExpertListItemDto[]
  const meta = apiResponse.meta; // Це об'єкт метаданих

  const totalExperts = parseInt(meta.total, 10);
  const limit = parseInt(meta.limit, 10);
  const currentPage = parseInt(meta.page, 10);
  const totalPages = Math.ceil(totalExperts / limit);

  return {
    data: experts,
    pagination: {
      total: totalExperts,
      pages: totalPages,
      currentPage: currentPage,
      limit: limit,
    },
  };
};

export const getMyExperts = async (status?: string): Promise<PaginatedExpertsResponse> => {
  const url = status ? `/experts/my?status=${status}` : '/experts/my';
  const response = await api.get<ApiResponse<ExpertListItemDto[]>>(url);
  const apiResponse = response.data; // Це об'єкт { success, data, meta }

  const experts = apiResponse.data; // Це масив ExpertListItemDto[]
  const meta = apiResponse.meta; // Це об'єкт метаданих

  const totalExperts = parseInt(meta.total, 10);
  const limit = parseInt(meta.limit, 10);
  const currentPage = parseInt(meta.page, 10);
  const totalPages = Math.ceil(totalExperts / limit);

  return {
    data: experts,
    pagination: {
      total: totalExperts,
      pages: totalPages,
      currentPage: currentPage,
      limit: limit,
    },
  };
};

export const getExpertById = async (id: number): Promise<ExpertDetailsDto> => {
  const response = await api.get<ApiResponse<ExpertDetailsDto>>(`/experts/${id}`);
  return unwrapApiResponse<ExpertDetailsDto>(response);
};

export const getSpecializations = async (): Promise<Specialization[]> => {
  const response = await api.get<ApiResponse<Specialization[]>>('/experts/specializations');
  return unwrapApiResponse<Specialization[]>(response);
};

// --- Appointment Endpoints ---

/**
 * Отримати доступні слоти для запису до конкретного експерта на певну дату
 * @param expertId - ID експерта
 * @param dateLdt - Цільова дата у форматі 'YYYY-MM-DD' (у локальному часі користувача)
 * @param clientId - (опціонально) ID клієнта для перевірки його доступності
 * @returns Список доступних слотів (у UTC часі)
 */
export const getAvailableSlots = async (expertId: number, dateLdt: string, clientId?: number): Promise<AvailableSlot[]> => {
  const params = new URLSearchParams({ dateLdt });
  if (clientId) {
    params.append('clientId', String(clientId));
  }
  const response = await api.get<ApiResponse<AvailableSlot[]>>(`/experts/${expertId}/available-slots?${params}`);
  return unwrapApiResponse<AvailableSlot[]>(response);
};

/**
 * Отримати доступні дні для конкретного експерта на певний місяць
 * @param expertId - ID експерта
 * @param month - Цільовий місяць у форматі 'YYYY-MM'
 * @returns Список доступних дат у вигляді рядків
 */
export const getAvailableDays = async (expertId: number, month: string): Promise<string[]> => {
  try {
    const response = await api.get<ApiResponse<string[]>>(`/experts/${expertId}/available-days?month=${month}`);
    return unwrapApiResponse<string[]>(response);
  } catch (error) {
    console.error(`Failed to fetch available days for expert ${expertId} in month ${month}. Returning an empty array to remove highlights.`, error);
    // If an error occurs, return an empty array to ensure no highlights/circles are shown.
    // The calendar component should ideally keep all days active by default, unless explicitly disabled by other means.
    return [];
  }
};

/**
 * Створити новий запис до експерта
 * @param expertId - ID експерта
 * @param data - Дані запису
 * @returns Створений запис
 */
export const createAppointment = async (expertId: number, data: CreateAppointmentDto): Promise<Appointment> => {
  const response = await api.post<ApiResponse<Appointment>>(`/experts/${expertId}/appointments`, data);
  return unwrapApiResponse<Appointment>(response);
};

export interface ExpertReview {
  id: number;
  appointmentId: number;
  client: { id: number; name: string; avatar?: string | null };
  rating: number;
  comment?: string | null;
  createdAt: string;
}

export const getExpertReviews = async (
  expertId: number,
  params: { page?: number; limit?: number } = {}
): Promise<{ data: ExpertReview[]; meta: { total: number; page: number; limit: number } }> => {
  const response = await api.get(`/experts/${expertId}/reviews`, { params: { page: params.page ?? 1, limit: params.limit ?? 10 } });
  return response.data;
};

export const getMyReviews = async (
  params: { page?: number; limit?: number } = {}
): Promise<{ data: ExpertReview[]; meta: { total: number; page: number; limit: number } }> => {
  const response = await api.get('/expert/reviews', { params: { page: params.page ?? 1, limit: params.limit ?? 10 } });
  return response.data;
};
